import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppPrefix } from '../AppPrefixContext';
import {
  CalendarIcon,
  MapPinIcon,
  TrophyIcon,
  UserGroupIcon,
  ChevronRightIcon,
  FlagIcon,
  ArrowRightIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import type { ContentItem, SlideContent } from '../../types/content';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface HeroContent {
  title: string;
  subtitle: string;
  tagline: string;
  video: { fallbackImage: string };
  details: { date: string; location: string; prize: string };
  registration: { openingDate: string };
}

interface TopPlayer {
  id: string;
  position: number;
  player_name: string;
  country: string;
  total_score: number;
  category: string;
  handicap?: number;
}

interface Champion {
  id: string;
  tournament_id: string;
  division: 'male' | 'female';
  player_name: string;
  player_country: string;
  score: string;
  player_image: string | null;
  highlights: string[];
}

interface PastTournament {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  venue: string | null;
  prize_pool: number;
  champions: Champion[];
}

interface PressArticle {
  id: string;
  source: string;
  title: string;
  date: string;
  quote: string;
  link: string;
  image_url: string | null;
}

const defaultHero: HeroContent = {
  title: 'IBOM OPEN 2025',
  subtitle: "Where Champions Are Made",
  tagline: "Nigeria's Premier Golf Tournament",
  video: { fallbackImage: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200' },
  details: { date: 'June 7-8, 2025', location: 'Ibom Golf & Country Club, Uyo', prize: '₦50M Prize Pool' },
  registration: { openingDate: '2025-04-23T00:00:00' }
};

function formatScore(score: number) {
  if (score === 0) return 'E';
  if (score >= 80) return '--';
  return score > 0 ? `+${score}` : score.toString();
}

function scoreColor(score: number) {
  if (score < 0) return 'text-green-500';
  if (score > 0) return 'text-red-500';
  return 'text-white';
}

export default function MobileHome() {
  const p = useAppPrefix();
  const navigate = useNavigate();
  const [heroContent, setHeroContent] = useState<HeroContent>(defaultHero);
  const [slides, setSlides] = useState<SlideContent[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [topPlayers, setTopPlayers] = useState<TopPlayer[]>([]);
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(false);
  const [pastTournaments, setPastTournaments] = useState<PastTournament[]>([]);
  const [recapIndex, setRecapIndex] = useState(0);
  const [pressArticles, setPressArticles] = useState<PressArticle[]>([]);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [registrationDate, setRegistrationDate] = useState<string>('');
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [registrationCount, setRegistrationCount] = useState(0);
  const [totalCapacity, setTotalCapacity] = useState(250);
  const [reservedSpots, setReservedSpots] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [sponsors, setSponsors] = useState<Array<{ id: string; name: string; logo_url: string; tier: string }>>([]);
  const [upcomingTournament, setUpcomingTournament] = useState<{ name: string; start_date: string; end_date: string; venue: string | null; prize_pool: number } | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<Array<{ id: string; title: string; date: string; time?: string; location?: string }>>([]);

  useEffect(() => {
    Promise.all([
      fetchHeroContent(),
      fetchTopPlayers(),
      fetchRegistrationData(),
      fetchSponsors(),
      fetchUpcomingTournament(),
      fetchSpotlightData(),
      fetchUpcomingEvents(),
    ]).finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const timer = setInterval(() => {
      setCurrentSlide(p => (p + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  useEffect(() => {
    if (pastTournaments.length < 2) return;
    const timer = setInterval(() => {
      setRecapIndex(p => (p + 1) % pastTournaments.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [pastTournaments.length]);

  const fetchHeroContent = async () => {
    try {
      const { data } = await supabase
        .from('content_sections')
        .select('*, content_items(*)')
        .eq('page', 'home')
        .eq('title', 'Hero Section')
        .eq('content_items.status', 'Published');

      if (data && data[0]?.content_items?.length) {
        const items = data[0].content_items as ContentItem[];
        const parse = (item: ContentItem | undefined) => {
          if (!item?.content) return null;
          if (typeof item.content === 'string') {
            try { return JSON.parse(item.content); } catch { return item.content.replace(/^"|"$/g, ''); }
          }
          return item.content;
        };

        const title = parse(items.find(i => i.title === 'Main Title')) ?? defaultHero.title;
        const subtitle = parse(items.find(i => i.title === 'Subtitle')) ?? defaultHero.subtitle;
        const tagline = parse(items.find(i => i.title === 'Tagline')) ?? defaultHero.tagline;
        const video = parse(items.find(i => i.title === 'Embedded Video')) ?? defaultHero.video;
        const details = parse(items.find(i => i.title === 'Event Details')) ?? defaultHero.details;
        const registration = parse(items.find(i => i.title === 'Registration Details')) ?? defaultHero.registration;

        setHeroContent({ title, subtitle, tagline, video, details, registration });
      }

      const { data: sliderData } = await supabase
        .from('content_sections')
        .select('*, content_items(*)')
        .eq('page', 'home')
        .eq('title', 'Hero Slider')
        .eq('content_items.status', 'Published');

      if (sliderData && sliderData[0]?.content_items?.length) {
        const parsed: SlideContent[] = [];
        for (const item of sliderData[0].content_items as ContentItem[]) {
          try {
            const c = typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
            parsed.push(c);
          } catch {}
        }
        setSlides(parsed);
      }
    } catch {}
  };

  const fetchUpcomingEvents = async () => {
    try {
      const todayStr = new Date().toISOString().split('T')[0];
      const { data: tourneys } = await supabase
        .from('tournaments')
        .select('id')
        .in('status', ['upcoming', 'active'])
        .order('start_date', { ascending: true })
        .limit(3);
      if (!tourneys?.length) return;
      const ids = tourneys.map((t: { id: string }) => t.id);
      const { data } = await supabase
        .from('tournament_events')
        .select('id, title, date, time, location')
        .in('tournament_id', ids)
        .gte('date', todayStr)
        .order('date', { ascending: true })
        .order('time', { ascending: true })
        .limit(3);
      if (data) setUpcomingEvents(data);
    } catch {}
  };

  const fetchUpcomingTournament = async () => {
    try {
      const { data } = await supabase
        .from('tournaments')
        .select('name, start_date, end_date, venue, prize_pool')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (data) setUpcomingTournament(data);
    } catch {}
  };

  const fetchTopPlayers = async () => {
    try {
      const { data: setting } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'leaderboard_published')
        .maybeSingle();

      const enabled = !setting || setting.value === true || setting.value === 'true';
      setLeaderboardEnabled(enabled);

      if (!enabled) return;

      const { data: tourneys } = await supabase
        .from('tournaments')
        .select('id')
        .in('status', ['ongoing', 'upcoming'])
        .order('start_date', { ascending: false })
        .limit(1);

      const currentTournamentId = tourneys?.[0]?.id;
      if (!currentTournamentId) return;

      const { data } = await supabase
        .from('leaderboard_view')
        .select('id, position, player_name, country, total_score, category, handicap')
        .eq('tournament_id', currentTournamentId)
        .gt('rounds_played', 0)
        .order('total_score', { ascending: true })
        .limit(5);
      if (data) setTopPlayers(data);
    } catch {}
  };

  const fetchSpotlightData = async () => {
    try {
      const { data: tourneys } = await supabase
        .from('tournaments')
        .select('id, name, start_date, end_date, venue, prize_pool')
        .eq('status', 'completed')
        .order('end_date', { ascending: false });

      if (tourneys && tourneys.length > 0) {
        const { data: allChamps } = await supabase
          .from('tournament_champions')
          .select('id, tournament_id, division, player_name, player_country, score, player_image, highlights')
          .in('tournament_id', tourneys.map(t => t.id));

        const withChamps = tourneys
          .map(t => ({ ...t, champions: allChamps?.filter(c => c.tournament_id === t.id) ?? [] }))
          .filter(t => t.champions.length > 0);

        setPastTournaments(withChamps);
      }

      const { data: articles } = await supabase
        .from('press_articles')
        .select('id, source, title, date, quote, link, image_url')
        .order('date', { ascending: false })
        .limit(3);

      if (articles) setPressArticles(articles);
    } catch {}
  };

  const fetchRegistrationData = async () => {
    try {
      const { data: regEnabledSetting } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'registration_enabled')
        .maybeSingle();

      if (regEnabledSetting?.value === false) {
        setRegistrationEnabled(false);
        return;
      }

      const { data: tourney } = await supabase
        .from('tournaments')
        .select('id, capacity, registration_open_date')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(1);

      if (tourney?.length) {
        setTotalCapacity(tourney[0].capacity);

        if (tourney[0].registration_open_date) {
          const dateStr = tourney[0].registration_open_date;
          const openingDate = new Date(dateStr);
          setRegistrationDate(dateStr);
          setIsRegistrationOpen(new Date() >= openingDate);
        }

        const { count } = await supabase
          .from('players')
          .select('*', { count: 'exact', head: true })
          .eq('tournament_id', tourney[0].id);
        if (count !== null) setRegistrationCount(count);
      }

      const { data: reservedData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'reserved_spots')
        .maybeSingle();

      if (reservedData?.value) {
        setReservedSpots(Number(reservedData.value));
      }
    } catch {}
  };

  const fetchSponsors = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data } = await supabase
        .from('partners')
        .select('id, company_name, logo_url, tier')
        .eq('status', 'active')
        .lte('start_date', today)
        .gte('end_date', today)
        .order('tier');
      if (data) setSponsors(data);
    } catch {}
  };

  useEffect(() => {
    if (!registrationDate || isRegistrationOpen) return;
    const calculate = () => {
      const diff = new Date(registrationDate).getTime() - Date.now();
      if (diff <= 0) {
        setIsRegistrationOpen(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    calculate();
    const timer = setInterval(calculate, 1000);
    return () => clearInterval(timer);
  }, [registrationDate, isRegistrationOpen]);

  const bgImage = slides[currentSlide]?.background?.url || heroContent.video.fallbackImage;
  const spotsLeft = totalCapacity - registrationCount - reservedSpots;
  const pct = Math.min(100, Math.round(((registrationCount + reservedSpots) / totalCapacity) * 100));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="pb-safe">
      {/* Hero */}
      <div className="relative h-[115vw] min-h-[400px] max-h-[600px] overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${bgImage})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 h-full flex flex-col justify-end px-4 pb-16 pt-16">
          <motion.span
            key={`tag-${currentSlide}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block self-start bg-golf-orange text-white text-[11px] font-semibold px-3 py-1 rounded-full mb-2"
          >
            {slides[currentSlide]?.subtitle || heroContent.tagline}
          </motion.span>
          <motion.h1
            key={`title-${currentSlide}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-display font-bold text-white text-2xl leading-tight"
          >
            {slides[currentSlide]?.title || heroContent.title}
          </motion.h1>
          <motion.p
            key={`sub-${currentSlide}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-golf-sand text-sm mt-1"
          >
            {slides[currentSlide]?.description || heroContent.subtitle}
          </motion.p>
        </div>

        {slides.length > 1 && (
          <div className="absolute bottom-10 right-4 z-10 flex gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentSlide ? 'bg-golf-orange' : 'bg-white/50'}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Quick Info Pills */}
      <div className="px-4 -mt-5 relative z-10 mb-4">
        <div className="bg-white rounded-2xl shadow-lg p-4 flex flex-col gap-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <CalendarIcon className="w-4 h-4 text-golf-orange flex-shrink-0" />
            <span>
              {upcomingTournament
                ? (() => {
                    const fmt = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
                    const s = new Date(upcomingTournament.start_date + 'T00:00:00');
                    const e = new Date(upcomingTournament.end_date + 'T00:00:00');
                    if (upcomingTournament.start_date === upcomingTournament.end_date) return fmt(upcomingTournament.start_date);
                    return `${s.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })} – ${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`;
                  })()
                : heroContent.details.date}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <MapPinIcon className="w-4 h-4 text-golf-orange flex-shrink-0" />
            <span className="truncate">
              {upcomingTournament?.venue ?? heroContent.details.location}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-golf-green font-semibold">
            <TrophyIcon className="w-4 h-4 text-golf-orange flex-shrink-0" />
            <span>
              {upcomingTournament
                ? `₦${Number(upcomingTournament.prize_pool).toLocaleString()} Prize Pool`
                : heroContent.details.prize}
            </span>
          </div>
        </div>
      </div>

      {/* Registration CTA */}
      {registrationEnabled && pct < 100 && (
        <div className="px-4 mb-5">
          <div className="bg-golf-green rounded-2xl p-4 text-white">
            {isRegistrationOpen ? (
              <>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs text-golf-sand uppercase tracking-wide font-medium mb-0.5">Registration</p>
                    <p className="font-display font-bold text-lg leading-tight">Open Now</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-display font-bold text-golf-orange">{spotsLeft}</p>
                    <p className="text-xs text-golf-sand">spots left</p>
                  </div>
                </div>
                <div className="w-full bg-white/20 rounded-full h-1.5 mb-2">
                  <div
                    className={`h-1.5 rounded-full transition-all ${pct >= 90 ? 'bg-red-400' : 'bg-golf-orange'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {pct >= 75 && (
                  <div className="flex items-center gap-1.5 text-amber-400 mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
                      <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                    </svg>
                    <p className="text-xs font-semibold">Hurry! Only {spotsLeft} spots remaining!</p>
                  </div>
                )}
                <Link
                  to={`${p}/registration`}
                  className="flex items-center justify-center gap-2 bg-golf-orange text-white font-semibold text-sm py-2.5 rounded-xl w-full transition-opacity active:opacity-80"
                >
                  Register Now
                  <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </>
            ) : (
              <>
                <p className="text-xs text-golf-sand uppercase tracking-wide font-medium mb-1">Registration Opens In</p>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[
                    { label: 'Days', value: timeLeft.days },
                    { label: 'Hrs', value: timeLeft.hours },
                    { label: 'Min', value: timeLeft.minutes },
                    { label: 'Sec', value: timeLeft.seconds },
                  ].map(item => (
                    <div key={item.label} className="bg-white/10 rounded-xl p-2 text-center">
                      <p className="font-display font-bold text-xl text-white leading-none">
                        {String(item.value).padStart(2, '0')}
                      </p>
                      <p className="text-[10px] text-golf-sand mt-0.5">{item.label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 bg-white/10 rounded-xl py-2.5 px-3 justify-center">
                  <CalendarIcon className="w-4 h-4 text-golf-sand" />
                  <p className="text-sm text-golf-sand font-medium">
                    {registrationDate
                      ? new Date(registrationDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                      : 'Coming Soon'}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <div className="px-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-golf-gray text-base">Upcoming Events</h2>
            <Link to={`${p}/calendar`} className="text-golf-orange text-sm font-medium flex items-center gap-1">
              View All <ChevronRightIcon className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex flex-col gap-2">
            {upcomingEvents.map(event => {
              const [y, m, d] = event.date.split('-').map(Number);
              const dateLabel = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
              return (
                <Link
                  key={event.id}
                  to={`${p}/calendar`}
                  className="flex items-start gap-3 bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 active:scale-[0.98] transition-transform"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                    <CalendarIcon className="w-5 h-5 text-golf-orange" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{event.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-golf-orange font-medium">{dateLabel}</span>
                      {event.time && (
                        <span className="flex items-center gap-0.5 text-xs text-gray-400">
                          <ClockIcon className="w-3 h-3" />
                          {event.time}
                        </span>
                      )}
                      {event.location && (
                        <span className="flex items-center gap-0.5 text-xs text-gray-400">
                          <MapPinIcon className="w-3 h-3" />
                          <span className="truncate max-w-[120px]">{event.location}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Leaderboard / Spotlight */}
      {leaderboardEnabled && topPlayers.length > 0 ? (
        <div className="px-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-golf-gray text-base">Leaderboard</h2>
            <Link to={`${p}/leaderboard`} className="text-golf-orange text-sm font-medium flex items-center gap-1">
              View All <ChevronRightIcon className="w-4 h-4" />
            </Link>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {topPlayers.map((player, i) => (
              <div
                key={player.id}
                onClick={() => navigate(`${p}/player/${player.id}`)}
                className={`flex items-center px-4 py-3 gap-3 cursor-pointer active:bg-gray-50 transition-colors ${i < topPlayers.length - 1 ? 'border-b border-gray-50' : ''} ${i === 0 ? 'bg-golf-orange/5' : ''}`}
              >
                <span className={`w-6 text-center font-bold text-sm ${i === 0 ? 'text-golf-orange' : 'text-gray-400'}`}>
                  {player.position}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${i === 0 ? 'text-golf-gray' : 'text-gray-700'}`}>
                    {player.player_name}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{player.country} · {player.category}</p>
                </div>
                <div
                  className="text-sm font-bold"
                  style={{ color: player.total_score < 0 ? '#22c55e' : player.total_score > 0 ? '#ef4444' : '#374151' }}
                >
                  {formatScore(player.total_score)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="px-4 mb-5 space-y-4">
          {/* Past Tournament Carousel */}
          {pastTournaments.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display font-bold text-golf-gray text-base">Tournament Recap</h2>
                <Link to={`${p}/history`} className="text-golf-orange text-sm font-medium flex items-center gap-1">
                  View History <ChevronRightIcon className="w-4 h-4" />
                </Link>
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={recapIndex}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.25 }}
                  className="bg-golf-green rounded-2xl p-4 text-white"
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-golf-sand uppercase tracking-wide font-medium">
                      {recapIndex === 0 ? 'Most Recent Tournament' : `Tournament ${recapIndex + 1} of ${pastTournaments.length}`}
                    </p>
                    {pastTournaments.length > 1 && (
                      <div className="flex gap-1">
                        {pastTournaments.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setRecapIndex(i)}
                            className={`w-1.5 h-1.5 rounded-full transition-colors ${i === recapIndex ? 'bg-golf-orange' : 'bg-white/30'}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="font-display font-bold text-lg leading-tight mb-1">{pastTournaments[recapIndex].name}</p>
                  <p className="text-sm text-golf-sand mb-3">
                    {new Date(pastTournaments[recapIndex].start_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {pastTournaments[recapIndex].venue ? ` · ${pastTournaments[recapIndex].venue}` : ''}
                  </p>
                  <div className="space-y-2">
                    {pastTournaments[recapIndex].champions.map(c => (
                      <div key={c.id} className="flex items-center gap-3 bg-white/10 rounded-xl px-3 py-2.5">
                        {c.player_image ? (
                          <img src={c.player_image} alt={c.player_name} className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-white/20" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-golf-orange/30 flex items-center justify-center flex-shrink-0">
                            <TrophyIcon className="w-4 h-4 text-golf-orange" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{c.player_name}</p>
                          <p className="text-xs text-golf-sand truncate">
                            {c.player_country} · {c.division === 'male' ? 'Men' : 'Women'}
                          </p>
                        </div>
                        <span className="text-golf-orange font-bold text-sm font-display">{c.score}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Link
                      to={`${p}/history`}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-white/10 text-white text-xs font-semibold active:bg-white/20 transition-colors"
                    >
                      <TrophyIcon className="w-3.5 h-3.5" />
                      History
                    </Link>
                    <Link
                      to={`${p}/history`}
                      state={{ tab: 'gallery', tournamentId: pastTournaments[recapIndex].id }}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-golf-orange text-white text-xs font-semibold active:opacity-80 transition-opacity"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                      Gallery
                    </Link>
                  </div>
                  {pastTournaments.length > 1 && (
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => setRecapIndex(i => Math.max(0, i - 1))}
                        disabled={recapIndex === 0}
                        className="flex-1 py-1.5 rounded-xl bg-white/10 text-white/70 text-xs font-medium disabled:opacity-30 active:bg-white/20 transition-colors"
                      >
                        ← Newer
                      </button>
                      <button
                        onClick={() => setRecapIndex(i => Math.min(pastTournaments.length - 1, i + 1))}
                        disabled={recapIndex === pastTournaments.length - 1}
                        className="flex-1 py-1.5 rounded-xl bg-white/10 text-white/70 text-xs font-medium disabled:opacity-30 active:bg-white/20 transition-colors"
                      >
                        Older →
                      </button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          )}

          {/* Press Coverage */}
          {pressArticles.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display font-bold text-golf-gray text-base">In the Press</h2>
                <Link to={`${p}/press`} className="text-golf-orange text-sm font-medium flex items-center gap-1">
                  View All <ChevronRightIcon className="w-4 h-4" />
                </Link>
              </div>
              <div className="space-y-3">
                {pressArticles.map(article => (
                  <a
                    key={article.id}
                    href={article.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-3 bg-white rounded-2xl border border-gray-100 shadow-sm p-3 active:scale-[0.98] transition-transform"
                  >
                    {article.image_url && (
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-semibold text-golf-orange uppercase tracking-wide mb-0.5">{article.source}</p>
                      <p className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2">{article.title}</p>
                      {article.quote && (
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2 italic">"{article.quote}"</p>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="px-4 mb-5">
        <h2 className="font-display font-bold text-golf-gray text-base mb-3">Quick Links</h2>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Schedule', path: `${p}/schedule`, icon: CalendarIcon, color: 'bg-blue-50 text-blue-600' },
            ...(leaderboardEnabled ? [{ label: 'Leaderboard', path: `${p}/leaderboard`, icon: ChartBarIcon, color: 'bg-green-50 text-green-600' }] : []),
            ...(isRegistrationOpen && registrationEnabled ? [{ label: 'Register', path: `${p}/registration`, icon: FlagIcon, color: 'bg-orange-50 text-golf-orange' }] : []),
            { label: 'Partners', path: `${p}/sponsors`, icon: UserGroupIcon, color: 'bg-rose-50 text-rose-600' },
          ].map(({ label, path, icon: Icon, color }) => (
            <Link
              key={path}
              to={path}
              className="flex items-center gap-3 bg-white border border-gray-100 rounded-2xl p-4 shadow-sm active:scale-95 transition-transform"
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <span className="font-semibold text-gray-700 text-sm">{label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Sponsors Strip */}
      {sponsors.length > 0 && (
        <div className="px-4 mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-golf-gray text-base">Our Partners</h2>
            <Link to={`${p}/sponsors`} className="text-golf-orange text-sm font-medium flex items-center gap-1">
              View All <ChevronRightIcon className="w-4 h-4" />
            </Link>
          </div>
          <div
            className="overflow-x-auto scrollbar-hide relative"
            onTouchStart={e => {
              const el = e.currentTarget.firstElementChild as HTMLElement;
              if (el) el.style.animationPlayState = 'paused';
            }}
            onTouchEnd={e => {
              const el = e.currentTarget.firstElementChild as HTMLElement;
              if (el) setTimeout(() => { el.style.animationPlayState = 'running'; }, 3000);
            }}
          >
            <div className="flex gap-3 animate-marquee w-max">
              {[...sponsors, ...sponsors].map((s, i) => (
                <div
                  key={`${s.id}-${i}`}
                  className="flex-shrink-0 w-20 h-14 bg-white rounded-xl border border-gray-100 flex items-center justify-center shadow-sm p-2"
                >
                  {s.logo_url ? (
                    <img src={s.logo_url} alt={s.company_name} className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-[10px] text-center text-gray-500 font-medium leading-tight">{s.company_name}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* About Teaser */}
      <div className="px-4 mb-8">
        <div
          className="relative rounded-2xl overflow-hidden h-44 bg-cover bg-center"
          style={{ backgroundImage: `url(https://images.pexels.com/photos/1325681/pexels-photo-1325681.jpeg?auto=compress&w=800)` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-golf-green/90 to-transparent" />
          <div className="relative z-10 p-5 h-full flex flex-col justify-between gap-3">
            <div>
              <p className="text-xs text-golf-sand font-medium uppercase tracking-wide">Ibom Golf & Country Club</p>
              <p className="text-white font-display font-bold text-base mt-1 leading-tight">Nigeria's Premier Golf Tournament</p>
            </div>
            <Link
              to={`${p}/about`}
              className="self-start flex items-center gap-1 text-golf-orange text-sm font-semibold"
            >
              Learn More <FlagIcon className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
